import { cn } from "@/lib/utils";
import { statusLabel } from "@/lib/constants";
import { AlertCircle, ChevronsUp, Minus, ChevronsDown } from "lucide-react";
import { TypeIcon } from "@/lib/activityTypes";

const base = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap";

const STATUS = {
  "To Do": "bg-slate-50 text-slate-600 border-slate-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  Waiting: "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const PRIORITY = {
  Urgent: "bg-red-50 text-red-700 border-red-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Medium: "bg-sky-50 text-sky-700 border-sky-200",
  Low: "bg-slate-50 text-slate-500 border-slate-200",
};

const HEALTH = {
  Strong: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Good: "bg-sky-50 text-sky-700 border-sky-200",
  "Needs Attention": "bg-amber-50 text-amber-700 border-amber-200",
  "At Risk": "bg-red-50 text-red-700 border-red-200",
  "N/A": "bg-slate-100 text-slate-400 border-slate-200",
};

const REL_STATUS = {
  "Key Partner": "bg-violet-50 text-violet-700 border-violet-200",
  "Standard Partner": "bg-sky-50 text-sky-700 border-sky-200",
  "Developing Relationship": "bg-teal-50 text-teal-700 border-teal-200",
  Prospect: "bg-slate-50 text-slate-600 border-slate-200",
  Dormant: "bg-slate-100 text-slate-500 border-slate-200",
  "Former Partner": "bg-slate-100 text-slate-400 border-slate-200",
  "N/A": "bg-slate-100 text-slate-400 border-slate-200",
};

export function StatusPill({ status, testid }) {
  return <span data-testid={testid} className={cn(base, STATUS[status] || STATUS["To Do"])}>{statusLabel(status)}</span>;
}
export function PriorityPill({ priority }) {
  if (!priority) return null;
  return <span className={cn(base, PRIORITY[priority] || PRIORITY.Medium)}>{priority}</span>;
}

const PRIORITY_ICON = {
  Urgent: { Icon: AlertCircle, cls: "text-red-600" },
  High: { Icon: ChevronsUp, cls: "text-orange-500" },
  Medium: { Icon: Minus, cls: "text-sky-600" },
  Low: { Icon: ChevronsDown, cls: "text-emerald-600" },
};
export function PriorityIcon({ priority, className }) {
  const p = PRIORITY_ICON[priority];
  if (!p) return null;
  const { Icon, cls } = p;
  return (
    <span title={`${priority} priority`} aria-label={`${priority} priority`} data-testid={`priority-icon-${priority.toLowerCase()}`} className="inline-flex shrink-0">
      <Icon className={cn("h-4 w-4", cls, className)} strokeWidth={2.75} />
    </span>
  );
}
export function HealthPill({ health }) {
  if (!health) return null;
  return <span className={cn(base, HEALTH[health] || HEALTH.Good)}>{health}</span>;
}
export function RelStatusPill({ status }) {
  if (!status) return null;
  return <span className={cn(base, REL_STATUS[status] || REL_STATUS.Prospect)}>{status}</span>;
}
export function TypePill({ type }) {
  if (!type) return null;
  return <span className={cn(base, "bg-slate-50 text-slate-600 border-slate-200")} data-testid="type-pill"><TypeIcon type={type} className="h-3 w-3" />{type}</span>;
}
export function OverduePill() {
  return <span className={cn(base, "bg-red-50 text-red-700 border-red-200")}>Overdue</span>;
}
